/**
 * Segment consent + Double Opt In events (SFCC-11017 / ANALYTICS-25, 26, 28).
 *
 * Magic strings verified verbatim against SFRA ground truth
 * (`org_hec/cartridge/scripts/segment/segmentLayerHelper.js:776-916`,
 * `controllers/Segment.js:17-71`) and hec-composable
 * (`overrides/app/utils/segment/eventHandlers.js`). Casing is load-bearing —
 * see the inline notes before changing any literal.
 *
 * Every path `await`s the email hash + `waitForAnalytics()`, THEN issues
 * `identify` and the `consent`/`Double Opt In` tracks synchronously, in order,
 * on the buffered analytics instance. That synchronous sequencing — no `await`
 * between `identify` and the tracks — is what guarantees FIFO enqueue order, and
 * it is load-bearing: a `consent` that ships before its `identify` has no userId
 * and is dropped source-side (SFCC-10693 / SFCC-10999). The hash is awaited only
 * to produce the userId, not to order the calls.
 *
 * The register path runs from the create-account success handler. In the common
 * drawer flow it redirects back to the CURRENT Workers route — a client-side
 * revalidation, no teardown — so delivery is unconstrained. Only the SFRA
 * redirect paths (checkout-mode → /checkout, empty/auth-context → /my-account)
 * are full-document navigations; there the enqueued events rely on Segment's
 * sendBeacon/keepalive to flush. Residual: the awaited hash defers the enqueue
 * by a microtask, so on those SFRA-teardown paths a consent could in principle
 * be lost if the navigation commits first (in practice the digest resolves
 * first) — still strictly better than the pre-fix state, which emitted no
 * register consent at all.
 *
 * If Segment never loads (for example WDH, which has no write key) the 5s
 * `waitForAnalytics` timeout lapses and the Segment calls are skipped; the GTM
 * `popupEmailSignup` push still fires (GTM tags self-gate via the gtag consent
 * defaults). TPA click-through consent is handled as downstream destination
 * context, not as a first-party Segment suppression gate.
 */
import type { AnalyticsBrowser } from "@segment/analytics-next";

import { pushDataLayer } from "~/hec/analytics/data-layer.client";
import { bySiteId } from "~/hec/utils/site-id";

import {
  formatUsPhone,
  getDeviceType,
  hashEmail,
  waitForAnalytics,
} from "./utils";

/** `valid_until` is ALWAYS this literal — SFRA never computes an expiry (`:784,814`). */
const VALID_UNTIL = "unlimited";

/**
 * Category suffixes — each has a LEADING SPACE because the category is
 * `sitePrefix + suffix` with no separator (`segmentLayerHelper.js:8-12`).
 * `"FSA" + " Marketing Email"` → `"FSA Marketing Email"`.
 */
const CATEGORY_SUFFIX = {
  marketing: " Marketing Email",
  sale: " Sale Email",
  education: " Education Email",
  textMessage: " Text Message Notification",
} as const;

export type ConsentAction = "accept" | "reject";

/**
 * SFRA's `placement` for consent captured on the account page
 * (`segmentLayerHelper.consentEventPlacement.fsaEasySpender = 'Accountpage'`,
 * used by `trackEasySpendForSegment`). Casing is load-bearing.
 */
export const ACCOUNT_PAGE_CONSENT_PLACEMENT = "Accountpage";

/** SFRA `handleEmailConsent` / `handleSMSConsent`: checkbox state → action. */
export const consentActionFor = (checked: boolean): ConsentAction =>
  checked ? "accept" : "reject";

/** Consent-category site prefix (FSA/HSA/WDH) via the exhaustive bySiteId map. */
function consentCategory(siteId: string, suffix: string): string {
  const prefix = bySiteId(siteId, {
    FSASTORE: "FSA",
    HSASTORE: "HSA",
    WDH: "WDH",
  });
  return `${prefix}${suffix}`;
}

/**
 * GTM `dataLayer` push — `popupEmailSignup` with the hashed email
 * (`controllers/Segment.js:52`). Runtime object push (not SSR HTML), so no
 * `</script>` escaping needed.
 */
export function pushPopupEmailSignup(hashedEmail: string): void {
  // COM-585: routed through the canonical helper so every client dataLayer
  // push goes through one seam. `popupEmailSignup` is NOT in the route-bound
  // allowlist — it is an identity-bearing consent event — so this is a pure
  // consolidation with no payload change.
  pushDataLayer({ event: "popupEmailSignup", hashedEmail });
}

/** One `consent` track for a single category suffix. */
function trackConsent(
  analytics: AnalyticsBrowser,
  siteId: string,
  placement: string,
  action: ConsentAction,
  suffix: string,
): void {
  analytics.track("consent", {
    action,
    valid_until: VALID_UNTIL,
    device: getDeviceType(),
    sales_channel: siteId,
    placement,
    category: consentCategory(siteId, suffix),
  });
}

/** HSA gate for the HSA-only Education category (`:895` `if (siteName === "HSA")`). */
function isHsaSite(siteId: string): boolean {
  return bySiteId(siteId, { FSASTORE: false, HSASTORE: true, WDH: false });
}

/**
 * Email-marketing `consent` — Marketing + Sale always, +Education on HSA.
 * Mirrors `handleEmailConsent` (`:889-898`), which emits 2–3 separate `consent`
 * calls. Used by the email banner per the SFCC-11017 banner decision.
 */
function trackEmailConsent(
  analytics: AnalyticsBrowser,
  siteId: string,
  placement: string,
  action: ConsentAction,
): void {
  trackConsent(analytics, siteId, placement, action, CATEGORY_SUFFIX.marketing);
  trackConsent(analytics, siteId, placement, action, CATEGORY_SUFFIX.sale);
  // ANALYTICS-26: HSA-only Education Email consent.
  if (isHsaSite(siteId)) {
    trackConsent(
      analytics,
      siteId,
      placement,
      action,
      CATEGORY_SUFFIX.education,
    );
  }
}

/**
 * SMS `Double Opt In` (`:800-820`). THREE distinct "action" fields: the implicit
 * Segment call-type (`track`), the hardcoded top-level `action: "new"`, and the
 * real accept/reject inside `consent_list[0].action`. `source` is `"page"`;
 * `consent_list` is a single-element array; `message` is always `""`.
 */
function trackDoubleOptIn(
  analytics: AnalyticsBrowser,
  phone: string,
  siteId: string,
  placement: string,
  action: ConsentAction,
): void {
  analytics.track("Double Opt In", {
    action: "new",
    source: "page",
    phone,
    consent_list: [
      {
        action,
        category: consentCategory(siteId, CATEGORY_SUFFIX.textMessage),
        message: "",
      },
    ],
    valid_until: VALID_UNTIL,
    device: getDeviceType(),
    sales_channel: siteId,
    placement,
  });
}

/**
 * Homepage email-banner / pop-up submit (ANALYTICS-28 + 25 + 26). Fires
 * `popupEmailSignup` (GTM, always) + Segment identify + email `consent`
 * (Marketing + Sale, +Education on HSA). Submitting IS the opt-in (per its
 * disclaimer) → `"accept"`. No Double Opt In — the banner collects no phone.
 *
 * SFCC-11936: `await waitForAnalytics()` before touching `window.analytics`. The
 * pop-up can be submitted within the first moments of a page load — before the
 * `<Segment />` effect has assigned `window.analytics` — and the old synchronous
 * `if (!window.analytics) return` silently dropped the `identify`/`consent` while
 * the `popupEmailSignup` pre-consent still fired. That produced the exact
 * "pre-consent fires, consent doesn't" regression (re-emergence of SFCC-10693).
 * The caller (Builder EmailSignup) does not await this or navigate, so waiting
 * is safe; mirrors composable's `await getSegmentReady()`.
 */
export async function fireEmailSignup({
  email,
  siteId,
  placement,
}: {
  email: string;
  siteId: string;
  placement: string;
}): Promise<void> {
  const hashedEmail = await hashEmail(email);
  pushPopupEmailSignup(hashedEmail);

  await waitForAnalytics();
  const analytics = window.analytics;
  if (!analytics) return;
  // Anonymous-subscriber identify: hashed email as userId, raw email as a trait.
  // Issued synchronously before the consent tracks (no await between), so
  // Segment's FIFO queue attaches the userId (SFCC-10693).
  analytics.identify(hashedEmail, {
    email,
    device: getDeviceType(),
    sales_channel: siteId,
  });
  trackEmailConsent(analytics, siteId, placement, "accept");
}

/**
 * Registration consent (SFCC-11945 — account-created). Restores the Segment
 * `consent` that disappeared when traffic cut over to Workers: identify, then
 * email `consent` (Marketing + Sale, +Education on HSA), then SMS `Double Opt In`
 * when the user opted into SMS AND gave a phone. `"accept"` throughout.
 *
 * Reference mix (flagged for martech confirmation on SFCC-11945):
 *  - EMAIL categories mirror COMPOSABLE `handleAccountCreationForSegment`
 *    (Marketing + Sale + Education), NOT the narrower SFRA
 *    `trackMessagingConsentForSegment` (Marketing only) — "parity = match
 *    production", composable being the live headless app we're restoring.
 *    Reverting to Marketing-only is a one-line swap (`trackEmailConsent` →
 *    `trackConsent(..., CATEGORY_SUFFIX.marketing)`).
 *  - SMS follows SFRA/accept-only semantics: a `Double Opt In` fires ONLY when
 *    SMS is opted-in with a phone. Composable additionally emits a `reject` when
 *    a phone is present but SMS is unchecked; we deliberately don't — telling
 *    Bloomreach a never-opted-in phone "rejected" SMS is noise.
 *
 * Called from the create-account success handler (account-drawer `RegisterForm`)
 * on server-confirmed success (avoids the duplicate-email false-positive that
 * composable's fire-on-submit has). Ordering + teardown behaviour: see the
 * module header — the common drawer flow revalidates in place (no teardown); the
 * SFRA-redirect flows (checkout-mode, auth-context) rely on sendBeacon.
 */
export async function fireRegisterConsent({
  email,
  smsOptIn,
  phone,
  siteId,
  placement,
}: {
  email: string;
  smsOptIn: boolean;
  phone?: string;
  siteId: string;
  placement: string;
}): Promise<void> {
  const hashedEmail = await hashEmail(email);
  await waitForAnalytics();
  const analytics = window.analytics;
  if (!analytics) return;
  // Identify synchronously before the consent tracks (no await between) so
  // Segment's FIFO queue attaches the userId (SFCC-10693).
  analytics.identify(hashedEmail, {
    email,
    device: getDeviceType(),
    sales_channel: siteId,
  });
  trackEmailConsent(analytics, siteId, placement, "accept");
  if (smsOptIn && phone) {
    trackDoubleOptIn(
      analytics,
      formatUsPhone(phone),
      siteId,
      placement,
      "accept",
    );
  }
}

/**
 * Account-page email / SMS opt-in submit (COM-417 item 6b). The ONE shared path
 * that emits `"reject"`: SFRA `handleEmailConsent` (`:889-898`) and
 * `handleSMSConsent` (`:908-917`) derive accept/reject from the checkbox, so
 * an unchecked box on save is an explicit reject — every other Workers caller
 * (banner, register, TPA) is an opt-in surface and stays `"accept"`.
 *
 *  - email: Marketing + Sale (+Education on HSA), `action` from `emailOptIn`.
 *  - SMS: `Double Opt In` ONLY when a phone is present (SFRA `if (!phone)
 *    return`), `consent_list[0].action` from `smsOptIn`.
 *
 * `placement` defaults to SFRA's account-page value; identify-before-consent
 * ordering as in the other paths (SFCC-10693). No account-page opt-in field
 * exists yet (item 6a) — the profile form wires this once it does.
 */
export async function fireAccountConsent({
  email,
  emailOptIn,
  smsOptIn,
  phone,
  siteId,
  placement = ACCOUNT_PAGE_CONSENT_PLACEMENT,
}: {
  email: string;
  emailOptIn: boolean;
  smsOptIn: boolean;
  phone?: string;
  siteId: string;
  placement?: string;
}): Promise<void> {
  const hashedEmail = await hashEmail(email);
  await waitForAnalytics();
  const analytics = window.analytics;
  if (!analytics) return;
  analytics.identify(hashedEmail, {
    email,
    device: getDeviceType(),
    sales_channel: siteId,
  });
  trackEmailConsent(analytics, siteId, placement, consentActionFor(emailOptIn));
  if (phone) {
    trackDoubleOptIn(
      analytics,
      formatUsPhone(phone),
      siteId,
      placement,
      consentActionFor(smsOptIn),
    );
  }
}

/**
 * Programmatic TPA marketing consent (SFCC-11945). A shopper who SSO's in from a
 * TPA granted marketing consent at the affiliate, so we assert it on their
 * behalf (email `consent`, `action: "accept"`) → Bloomreach subscribes them.
 * Ports hec-composable `TpaConsentTracker` → `trackConsent`, which the Workers
 * app never carried over; the emission disappeared at the cutover.
 *
 * `placement` distinguishes the two composable cases (`TpaConsentTracker.js`):
 *  - `"tpa-initial-load"` — asserted on the marketing-allowed SSO arrival.
 *  - `"tpa-consent-change"` — a shopper who arrived NOT-yet-allowed and then
 *    opted in mid-session (e.g. the TPA welcome-modal checkbox).
 *
 * Programmatic (no user action), so identify-before-consent matters as much as
 * for the form paths — the SSO customer may not be identified yet (loader
 * revalidation is async), so identify here rather than relying on
 * `<SegmentIdentify />`. Marketing + Sale (+Education on HSA), matching the
 * email-consent set the other placements use.
 */
export async function fireTpaConsent({
  email,
  siteId,
  placement,
}: {
  email: string;
  siteId: string;
  placement: "tpa-initial-load" | "tpa-consent-change";
}): Promise<void> {
  const hashedEmail = await hashEmail(email);
  await waitForAnalytics();
  const analytics = window.analytics;
  if (!analytics) return;
  // Identify synchronously before the consent tracks (no await between) so
  // Segment's FIFO queue attaches the userId (SFCC-10693).
  analytics.identify(hashedEmail, {
    email,
    device: getDeviceType(),
    sales_channel: siteId,
  });
  trackEmailConsent(analytics, siteId, placement, "accept");
}
